import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, ClipboardList, Loader2 } from "lucide-react";

import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { DGI_CONFIG_TRAIL } from "@/components/onboarding/OnboardingTrail";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { SettingsCaeCard } from "@/pages/settings/SettingsCaeCard";

export interface OnboardingCaeStepProps {
  missingCfeTypeCodes: string[];
  /** True while CAE list is refetching after add/delete (badges sync with table). */
  isCaeListRefreshing: boolean;
  /** Back nav (renders left "Volver" button when provided). */
  onBack?: () => void;
  /** Next nav (renders right "Siguiente" button when provided). */
  onNext?: () => void | Promise<void>;
  /** Disable Next while still resolving (e.g., missing CAE types). */
  nextDisabled?: boolean;
  /** Show a spinner on Next while it works. */
  nextLoading?: boolean;
}

function CaeBrandColumn(): JSX.Element {
  const { t } = useTranslation();
  return (
    <OnboardingBrandColumn
      eyebrow={t(
        "onboarding.caeStep.brandEyebrow",
        "Autorizaciones de DGI",
      )}
      quote={
        <>
          {t("onboarding.caeStep.brandQuoteLead", "Cargamos tus CAEs —")}{" "}
          <span className="accent">
            {t(
              "onboarding.caeStep.brandQuoteAccent",
              "para numerar cada comprobante con la autorización correcta.",
            )}
          </span>
        </>
      }
      currentStep={3}
      trailSteps={DGI_CONFIG_TRAIL}
    />
  );
}

export function OnboardingCaeStep({
  missingCfeTypeCodes,
  isCaeListRefreshing,
  onBack,
  onNext,
  nextDisabled = false,
  nextLoading = false,
}: OnboardingCaeStepProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={3}
            total={3}
            label={t("onboarding.caeStep.tag", "CAEs")}
            flow={t("onboarding.caeStep.flow", "Configuración DGI")}
          />
          <h1 className="fx-onb-h1">
            {t("onboarding.caeStep.h1Lead", "Agregá tus")}{" "}
            <span className="ital">
              {t(
                "onboarding.caeStep.h1Accent",
                "autorizaciones CAE.",
              )}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.caeStep.lead",
              "Cargá el XML de cada CAE que DGI te asignó. Necesitamos una autorización por cada tipo de comprobante que vayas a emitir.",
            )}
          </p>

          {missingCfeTypeCodes.length > 0 ? (
            <Alert
              variant="warning"
              className="mb-6 items-start border-amber-200/80 px-4 py-3.5 shadow-sm transition-colors duration-150 dark:border-amber-800/80"
            >
              <ClipboardList
                className="mt-0.5 size-5 shrink-0 text-amber-700 dark:text-amber-400"
                aria-hidden
              />
              <div className="min-w-0 flex-1 space-y-2">
                <AlertTitle className="text-base font-semibold leading-snug text-amber-950 dark:text-amber-100">
                  {t("onboarding.caeRequiredTypesTitle")}
                </AlertTitle>
                <AlertDescription className="space-y-3 text-amber-900/90 dark:text-amber-200/95">
                  <p className="text-sm leading-relaxed">
                    {t("onboarding.caeRequiredTypesHint")}
                  </p>
                  <div
                    className={`flex flex-wrap items-center gap-2 transition-opacity duration-150 motion-reduce:transition-none ${
                      isCaeListRefreshing ? "opacity-60" : ""
                    }`}
                  >
                    {isCaeListRefreshing ? (
                      <Loader2
                        className="size-4 shrink-0 animate-spin text-amber-700 dark:text-amber-400"
                        aria-hidden
                      />
                    ) : null}
                    {missingCfeTypeCodes.map((code) => (
                      <Badge
                        key={code}
                        variant="outline"
                        className="border-amber-300/80 bg-background/60 px-2.5 py-0.5 text-xs font-medium text-amber-950 transition-colors dark:border-amber-700 dark:text-amber-50"
                      >
                        {t(`invoices.cfeTypes.${code}`)}
                      </Badge>
                    ))}
                  </div>
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          <SettingsCaeCard />

          {(onBack || onNext) && (
            <div className="fx-onb-actions" style={{ marginTop: 28 }}>
              <div className="side">
                {onBack ? (
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onBack}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                ) : null}
              </div>
              <div className="side end">
                {onNext ? (
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-accent"
                    onClick={() => void onNext()}
                    disabled={nextDisabled || nextLoading}
                  >
                    {nextLoading ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {t("onboarding.next", "Siguiente")}
                    <ArrowRight aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </>
      }
      brand={<CaeBrandColumn />}
    />
  );
}
